/**
 * f11.6: the amber banner above the grid. "N lines need a check. Saving is
 * never blocked", one filter chip per code with a count, and the "Show only
 * lines with warnings" toggle. Every count is the server's `validation_summary`.
 * Non-modal and never the danger style: this is advice, not a refusal.
 */

import { AlertTriangle } from 'lucide-react';

import type { ValidationSummary, ValidationWarningCode } from '@/api/client';

import type { WarningFilter } from './useWarningFilter';

interface ValidationBannerProps {
  summary: ValidationSummary | undefined;
  filter: WarningFilter;
  onToggleCode: (code: ValidationWarningCode) => void;
  onOnlyWarnings: (on: boolean) => void;
}

export function ValidationBanner({ summary, filter, onToggleCode, onOnlyWarnings }: ValidationBannerProps) {
  if (!summary || summary.lines_with_warnings <= 0) return null;
  const count = summary.lines_with_warnings;
  return (
    <section className="vw-banner" aria-label="Validation warnings" data-severity="warning">
      <span className="vw-banner-icon" aria-hidden="true">
        <AlertTriangle />
      </span>
      <div className="vw-banner-body">
        <p className="vw-banner-title">
          {count} {count === 1 ? 'line needs' : 'lines need'} a check. Saving is never blocked
        </p>
        <div className="vw-chips">
          {summary.by_code
            .filter((entry) => entry.line_count > 0)
            .map((entry) => (
              <button
                key={entry.code}
                type="button"
                className="vw-chip"
                aria-pressed={filter.code === entry.code}
                onClick={() => onToggleCode(entry.code)}
              >
                {entry.label} · {entry.line_count}
              </button>
            ))}
        </div>
      </div>
      <label className="vw-toggle">
        <input
          type="checkbox"
          checked={filter.onlyWarnings}
          onChange={(event) => onOnlyWarnings(event.target.checked)}
        />
        <span>Show only lines with warnings</span>
      </label>
    </section>
  );
}
